/**
 * Typed CRUD wrappers around every backend endpoint. orgId flows through
 * the URL path: /api/v1/org/{orgId}/<resource>... (IGNODE convention).
 */
import { apiFlatFetch, apiOrgFetch, orgStreamUrl } from './apiClient';
import type {
    BoundingBox,
    BulkDeleteResponse,
    Class,
    ImageMeta,
    ImageStatus,
    LockBody,
    LockResponse,
    PresignResponse,
    Project,
    PublicConfig,
    Quota,
    Snapshot,
    SplitSummary,
    UnlockResponse,
    VersionInfo,
} from '../types/api';

// ---------- Projects ----------

export interface CreateProjectBody {
    name: string;
    description?: string;
}
export interface UpdateProjectBody {
    name?: string;
    description?: string;
}

export const projectsApi = {
    list: (orgId?: string) =>
        apiOrgFetch<Project[]>('/projects', orgId),
    create: (body: CreateProjectBody, orgId?: string) =>
        apiOrgFetch<Project>('/projects', orgId, { method: 'POST', body }),
    get: (id: string, orgId?: string) =>
        apiOrgFetch<Project>(`/projects/${id}`, orgId),
    update: (id: string, body: UpdateProjectBody, orgId?: string) =>
        apiOrgFetch<Project>(`/projects/${id}`, orgId, { method: 'PATCH', body }),
    remove: (id: string, orgId?: string) =>
        apiOrgFetch<void>(`/projects/${id}`, orgId, { method: 'DELETE' }),
    quota: (id: string, orgId?: string) =>
        apiOrgFetch<Quota>(`/projects/${id}/quota`, orgId),
    /** Batch B + G.6 — flip is_locked=true AND atomically generate a
     *  snapshot. Optional body picks the train/val/test split; backend
     *  defaults to 80/15/5 stratified seed=42 when omitted. */
    lock: (id: string, orgId?: string, body?: LockBody) =>
        apiOrgFetch<LockResponse>(`/projects/${id}/lock`, orgId, {
            method: 'POST',
            body: body ?? {},
        }),
    /** Batch B — clear is_locked + delete snapshot row + best-effort S3 cleanup. */
    unlock: (id: string, orgId?: string) =>
        apiOrgFetch<UnlockResponse>(`/projects/${id}/unlock`, orgId, { method: 'POST' }),
    /** G.6 — recompute the class-coverage preview for a proposed split.
     *  Powers the Lock modal's live class-coverage table as the customer
     *  adjusts the ratio slider. Same backend algorithm runs at lock time. */
    stratifyPreview: (
        id: string,
        body: LockBody['split'] | undefined,
        orgId?: string,
    ) =>
        apiOrgFetch<SplitSummary>(`/projects/${id}/stratify-preview`, orgId, {
            method: 'POST',
            body: body ?? {},
        }),
    /** Deep-copy a project: new project + images (S3 originals + thumbnails)
     *  + boxes. Classes, snapshots, lock state are NOT carried over — the
     *  duplicate starts fresh and unlocked. New project's name is set
     *  server-side ("<src> (copy)", "(copy 2)", … on collisions). */
    duplicate: (id: string, orgId?: string) =>
        apiOrgFetch<Project>(`/projects/${id}/duplicate`, orgId, { method: 'POST' }),
    /** Project Import — POST an IGNODE Archive (.tar) to recreate a
     *  project + classes + images + boxes. New project starts unlocked
     *  with no snapshot. Optional name override defaults to manifest's
     *  project_name; server appends "(imported)" suffix on collision. */
    importArchive: async (file: File, name?: string, orgId?: string) => {
        const fd = new FormData();
        fd.append('archive', file);
        if (name && name.trim()) fd.append('name', name.trim());
        return apiOrgFetch<Project>('/projects/import', orgId, {
            method: 'POST',
            body: fd,
        });
    },
};

// ---------- Classes ----------

export interface CreateClassBody {
    name: string;
    color?: string;
}
export interface UpdateClassBody {
    name?: string;
    color?: string;
}

export const classesApi = {
    list: (projectId: string, orgId?: string) =>
        apiOrgFetch<Class[]>(`/projects/${projectId}/classes`, orgId),
    create: (projectId: string, body: CreateClassBody, orgId?: string) =>
        apiOrgFetch<Class>(`/projects/${projectId}/classes`, orgId, { method: 'POST', body }),
    update: (projectId: string, idx: number, body: UpdateClassBody, orgId?: string) =>
        apiOrgFetch<Class>(`/projects/${projectId}/classes/${idx}`, orgId, { method: 'PATCH', body }),
    remove: (projectId: string, idx: number, orgId?: string) =>
        apiOrgFetch<void>(`/projects/${projectId}/classes/${idx}`, orgId, { method: 'DELETE' }),
};

// ---------- Images ----------

export const imagesApi = {
    list: (
        projectId: string,
        filters: { status?: ImageStatus | undefined; classIndex?: number | undefined },
        orgId?: string,
    ) =>
        apiOrgFetch<ImageMeta[]>(`/projects/${projectId}/images`, orgId, {
            query: {
                status: filters.status,
                class_index: filters.classIndex,
            },
        }),
    get: (id: string, orgId?: string) =>
        apiOrgFetch<ImageMeta>(`/images/${id}`, orgId),
    /**
     * Legacy single-shot upload. POST multipart → backend buffers bytes →
     * backend streams to S3. Kept as a fallback for clients that haven't
     * been switched to presign+finalize.
     */
    upload: async (projectId: string, file: File, orgId?: string) => {
        const fd = new FormData();
        fd.append('file', file);
        return apiOrgFetch<ImageMeta>(`/projects/${projectId}/images`, orgId, {
            method: 'POST',
            body: fd,
        });
    },
    /**
     * IR-3.D.13 step 1/2 — reserve an image row (state='pending') and mint
     * the URL the browser should PUT bytes to. The URL is either a direct
     * S3 presigned PUT (s3_mode=direct) or a backend upload-proxy URL
     * (s3_mode=proxy). The client doesn't branch on mode; it just PUTs
     * whatever URL the server returns.
     */
    presign: (
        projectId: string,
        body: { filename: string; size: number; content_type: string },
        orgId?: string,
    ) =>
        apiOrgFetch<PresignResponse>(`/projects/${projectId}/images/presign`, orgId, {
            method: 'POST',
            body,
        }),
    /**
     * IR-3.D.13 step 2/2 — after a successful PUT, tell the backend the
     * upload is done. Backend HEADs the object, decodes width/height, and
     * flips state='ready'. Idempotent — re-calling returns the same
     * ImageMeta.
     */
    finalize: (imageId: string, orgId?: string) =>
        apiOrgFetch<ImageMeta>(`/images/${imageId}/finalize`, orgId, { method: 'POST' }),
    /** Browser URL for streaming original bytes (Canvas <img src=...>). */
    bytesUrl: (id: string, orgId?: string) =>
        orgStreamUrl(orgId, `/images/${id}/bytes`),
    /** Browser URL for streaming thumbnail (grid <img src=...>). */
    thumbnailUrl: (id: string, orgId?: string) =>
        orgStreamUrl(orgId, `/images/${id}/thumbnail`),
    remove: (id: string, orgId?: string) =>
        apiOrgFetch<void>(`/images/${id}`, orgId, { method: 'DELETE' }),
    /** Rename — PATCH /images/:id with {filename}. Lock-gated; the
     *  S3 key is untouched (downloads use the DB filename via
     *  Content-Disposition so the rename takes effect immediately). */
    rename: (id: string, filename: string, orgId?: string) =>
        apiOrgFetch<ImageMeta>(`/images/${id}`, orgId, {
            method: 'PATCH',
            body: { filename },
        }),
    /** D.14.1 — delete N images in one tx. Boxes cascade via FK. Server
     *  returns the IDs it actually deleted + IDs that didn't exist (so the
     *  UI can finish optimistic removal cleanly). 200-row cap server-side. */
    bulkRemove: (projectId: string, ids: string[], orgId?: string) =>
        apiOrgFetch<BulkDeleteResponse>(`/projects/${projectId}/images/bulk-delete`, orgId, {
            method: 'POST',
            body: { image_ids: ids },
        }),
    /** Browser URL for the bulk-download ZIP of every original image in
     *  the project. Server streams via io.Pipe — pod memory stays bounded. */
    downloadAllUrl: (projectId: string, orgId?: string) =>
        orgStreamUrl(orgId, `/projects/${projectId}/images/download`),
};

// ---------- Boxes ----------

export interface CreateBoxBody {
    class_index: number;
    label?: string;
    x_center: number;
    y_center: number;
    width: number;
    height: number;
}
export interface UpdateBoxBody {
    class_index?: number;
    /** Empty string clears the label; non-empty sets; omit or null leaves alone. */
    label?: string | null;
    z_order?: number;
    /** Server-resolved relative bump. Use this for the canonical Bring-to-Front /
     *  Send-to-Back actions so the client doesn't have to know the current
     *  z_order range. */
    z_order_to?: 'front' | 'back';
    x_center?: number;
    y_center?: number;
    width?: number;
    height?: number;
}

export const boxesApi = {
    list: (imageId: string, orgId?: string) =>
        apiOrgFetch<BoundingBox[]>(`/images/${imageId}/boxes`, orgId),
    create: (imageId: string, body: CreateBoxBody, orgId?: string) =>
        apiOrgFetch<BoundingBox>(`/images/${imageId}/boxes`, orgId, { method: 'POST', body }),
    update: (id: string, body: UpdateBoxBody, orgId?: string) =>
        apiOrgFetch<BoundingBox>(`/boxes/${id}`, orgId, { method: 'PATCH', body }),
    remove: (id: string, orgId?: string) =>
        apiOrgFetch<void>(`/boxes/${id}`, orgId, { method: 'DELETE' }),
    /** Atomically rewrite z_order for every box on an image to match the
     *  supplied list. `boxIds` is in render order (low z first = back of
     *  stack). Server returns the boxes in the new order. Use for
     *  multi-position moves: to-front/to-back, drag-reorder, future bulk ops. */
    reorder: (imageId: string, boxIds: string[], orgId?: string) =>
        apiOrgFetch<BoundingBox[]>(`/images/${imageId}/boxes/order`, orgId, {
            method: 'PUT',
            body: { box_ids: boxIds },
        }),
    /** Hot-path companion to `reorder` — O(1) wire size. Server atomically
     *  swaps the z_order of two boxes on the same image (handles the tied-z
     *  case by renormalizing internally). Returns the image's full box list
     *  in canonical order so the caller can replace local state. */
    swap: (aId: string, bId: string, orgId?: string) =>
        apiOrgFetch<BoundingBox[]>(`/boxes/swap`, orgId, {
            method: 'PUT',
            body: { a_id: aId, b_id: bId },
        }),
};

// ---------- Snapshots ----------

export const snapshotsApi = {
    list: (projectId: string, orgId?: string) =>
        apiOrgFetch<Snapshot[]>(`/projects/${projectId}/snapshots`, orgId),
    create: (projectId: string, orgId?: string) =>
        apiOrgFetch<Snapshot>(`/projects/${projectId}/snapshots`, orgId, { method: 'POST' }),
    get: (id: string, orgId?: string) =>
        apiOrgFetch<Snapshot>(`/snapshots/${id}`, orgId),
    downloadUrl: (
        id: string,
        orgId?: string,
        opts?: { format?: 'ignode' | 'yolo' | 'coco'; container?: 'tar' | 'zip' },
    ) => {
        // G.7 — compose the download URL with optional format + container
        // query params so the customer can pick from the ExportDatasetModal.
        // Backend defaults to ignore-the-params when absent (= ignode+tar).
        const base = orgStreamUrl(orgId, `/snapshots/${id}/download`);
        const params: string[] = [];
        if (opts?.format && opts.format !== 'ignode') params.push(`format=${opts.format}`);
        else if (opts?.format === 'ignode') params.push('format=ignode');
        if (opts?.container && opts.container !== 'tar') params.push(`container=${opts.container}`);
        else if (opts?.container === 'tar') params.push('container=tar');
        return params.length > 0 ? `${base}?${params.join('&')}` : base;
    },
    /** G.6 — persisted per-class breakdown of the train/val/test split.
     *  Returns same wire shape as stratify-preview so the
     *  ClassCoveragePreview component renders it without branching.
     *  Used by the project header banner, post-lock confirmation modal,
     *  and the Detection wizard project-picker preview pane. */
    splitSummary: (id: string, orgId?: string) =>
        apiOrgFetch<SplitSummary>(`/snapshots/${id}/split-summary`, orgId),
};

// ---------- Meta (version + license) ----------

export const metaApi = {
    version: () => apiFlatFetch<VersionInfo>('/api/v1/version'),
    // Licence is deployment-wide (single row in ig_annotation_license).
    // The orgId path segment is shape-only — the backend ignores it.
    // Keeping the URL shape lets cookie + standalone use the same client
    // path. On IGNODE, the LF proxy skips its per-org permission check
    // for this exact path.
    license: (orgId?: string) =>
        apiOrgFetch<Record<string, unknown>>('/license', orgId),
};

// ---------- Config ----------
//
// `get()` is the unauth platform-default endpoint — used before orgId is
// known (boot screen, standalone). `getForOrg(orgId)` is auth-required
// and resolves effective quotas through the per-org → platform → env
// fallback chain (D.2.7).
export const configApi = {
    get: () => apiFlatFetch<PublicConfig>('/api/v1/config'),
    getForOrg: (orgId: string) => apiOrgFetch<PublicConfig>('/config', orgId),
};
